// Mirrors ProductivityView in apps/api/src/integrators/productivity/productivity.view.ts; the API writes every string.

export type ProductivityPeriodKey = 'baseline' | 'first_ai' | 'current';

export interface ProductivityStat {
  value: string;
  label: string;
  sub: string;
  lead: boolean;
}

export interface ProductivityBar {
  name: string;
  sub: string;
  value: number;
  display: string;
}

export interface ProductivityFigure {
  title: string;
  unit: string;
  delta: string;
  bars: ProductivityBar[];
  caption: string;
}

export type ProductivityMixKey = 'both' | 'aionly' | 'colonly' | 'none';

export interface ProductivityMix {
  key: ProductivityMixKey;
  label: string;
  share: number;
  display: string;
  showInBar: boolean;
  legend: string;
}

export interface ProductivityMonth {
  month: string;
  /** Percent of that month's developer PRs, null for a month with none. */
  aiShare: number | null;
  colleagueShare: number | null;
}

export interface ProductivityRow {
  dimension: string;
  before: string;
  now: string;
  change: string;
  evidence: 'measured' | 'next';
  muted: boolean;
}

export interface ProductivityItem {
  title: string;
  body: string;
}

export interface ProductivityReportView {
  /** Absent from views built before the metrics mode; `metrics` has no before-AI baseline. */
  mode?: 'comparison' | 'metrics';
  header: { title: string; who: string; measured: string; mergedPullRequests: string; repositories: string };
  glance: { title: string; stats: ProductivityStat[] };
  stepChange?: { title: string; lead: string; figures: ProductivityFigure[] };
  review: {
    title: string;
    lead: string;
    figure: {
      title: string;
      axis: string;
      legend: { ai: string; colleague: string };
      periods: { key: ProductivityPeriodKey; label: string; from: string; to: string }[];
      months: ProductivityMonth[];
      mix: ProductivityMix[];
      caption: string;
    };
  };
  gains?: { title: string; lead: string; columns: string[]; rows: ProductivityRow[] };
  measured: { title: string; items: ProductivityItem[] };
  notClaiming: { title: string; lead: string; items: ProductivityItem[] };
  footer: string;
}

// The four older codes are still read from views an API built before the metrics mode.
export type NotReportableReason =
  | 'noDeveloperPrs'
  | 'fewerThanTwoPeriods'
  | 'noAiBoundary'
  | 'noBaseline'
  | 'emptyPeriod';

export interface ProductivityNotReportable {
  notReportable: true;
  reason: NotReportableReason;
}

export type ProductivityView = ProductivityReportView | ProductivityNotReportable;

export const isNotReportable = (view: ProductivityView): view is ProductivityNotReportable =>
  'notReportable' in view && view.notReportable === true;

// ponytail: cast, not normalised like the quality audit: this body is built by the API in the same repo, not a skill's file.
export const productivityView = (report?: Record<string, unknown>): ProductivityView | null =>
  report ? (report as unknown as ProductivityView) : null;
